import { motion } from 'framer-motion';

const VALUES = [
  { num: '01', title: 'Reliability',  short: 'No dry runs. Period.' },
  { num: '02', title: 'Speed',        short: 'We move faster.' },
  { num: '03', title: 'Community',    short: 'We grow as one.' },
  { num: '04', title: 'Fair Prices',  short: 'No markups. No games.' },
];

export default function About() {
  return (
    <div className="bce-page" style={{ background: 'var(--bce-bg)' }}>

      {/* ── HERO ── */}
      <section style={{
        padding: '6rem 0 5rem',
        borderBottom: '1px solid var(--bce-border)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <svg aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hatch-bce" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(-48 0 0)">
              <line x1="0" y1="0" x2="0" y2="7" stroke="#F5A800" strokeWidth="0.7" />
            </pattern>
          </defs>
          <text fill="url(#hatch-bce)" opacity="0.11" fontFamily="'Bebas Neue', sans-serif" fontSize="340" x="-12" y="300" letterSpacing="0.03em">BCE</text>
          <text fill="url(#hatch-bce)" opacity="0.055" fontFamily="'Bebas Neue', sans-serif" fontSize="180" x="52%" y="88%" letterSpacing="0.05em">BIG COUNTRY</text>
        </svg>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 50% 55% at 6% 0%, rgba(245,168,0,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="bce-container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
            style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
            <div style={{ width: 30, height: 2, background: 'var(--bce-gold)' }} />
            <span className="bce-label">About Big Country Energy</span>
          </motion.div>

          {['BIGGER.', 'STRONGER.', 'UNITED.'].map((word, i) => (
            <div key={word} style={{ overflow: 'hidden' }}>
              <motion.h1 className="bce-display"
                initial={{ y: '100%' }} animate={{ y: 0 }}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                style={{ fontSize: 'clamp(3.5rem, 10vw, 9rem)', color: i === 2 ? 'var(--bce-gold)' : 'var(--bce-white)', lineHeight: 0.95, marginBottom: '0.15em' }}>
                {word}
              </motion.h1>
            </div>
          ))}

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            style={{ maxWidth: 600, color: 'var(--bce-text)', lineHeight: 1.8, marginTop: '2rem', fontSize: 15 }}>
            Big Country Energy is the backbone of fuel and industrial supply operations. Founded on the
            principles of reliability, speed, and community — we exist to keep your engine running, no matter what.
          </motion.p>
        </div>
      </section>

      {/* ── OUR VALUES — BORDERED RECTANGLES ── */}
      <section style={{ padding: '6rem 0' }}>
        <div className="bce-container">
          <div className="bce-section-label" style={{ marginBottom: '2.5rem' }}><span>Our Values</span></div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1px',
            background: 'rgba(245,168,0,0.08)',
          }}>
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ x: 5 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ delay: i * 0.1, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  background: '#080808',
                  padding: '2.75rem 2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  borderTop: '2px solid var(--bce-gold)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                }}
              >
                {/* Ghost number in background */}
                <div style={{
                  position: 'absolute',
                  right: '-4px',
                  bottom: '-20px',
                  fontFamily: 'var(--font-display)',
                  fontSize: '8rem',
                  lineHeight: 1,
                  color: 'rgba(245,168,0,0.04)',
                  userSelect: 'none',
                  pointerEvents: 'none',
                  letterSpacing: '0.02em',
                }}>
                  {v.num}
                </div>

                {/* Number */}
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(2rem, 3vw, 3rem)',
                  color: 'rgba(245,168,0,0.35)',
                  lineHeight: 1,
                  flexShrink: 0,
                  minWidth: '3rem',
                  letterSpacing: '0.02em',
                }}>
                  {v.num}
                </div>

                {/* Vertical separator */}
                <div style={{
                  width: 1,
                  alignSelf: 'stretch',
                  background: 'rgba(245,168,0,0.15)',
                  flexShrink: 0,
                }} />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(1.6rem, 2.5vw, 2.2rem)',
                    color: 'var(--bce-white)',
                    letterSpacing: '0.03em',
                    lineHeight: 1,
                    marginBottom: '0.5rem',
                  }}>
                    {v.title.toUpperCase()}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-condensed)',
                    fontSize: 11,
                    color: 'rgba(245,168,0,0.5)',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                  }}>
                    {v.short}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT WE SUPPLY ── */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
        style={{ padding: '4rem 0 6rem', background: 'var(--bce-bg-card)', borderTop: '1px solid var(--bce-border)' }}
      >
        <div className="bce-container">
          <motion.div
            initial={{ opacity: 0, x: -14 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="bce-section-label"
          >
            <span>What We Supply</span>
          </motion.div>
          {[
            'From Jerry Cans to Refined Oil, Ethanol to Nitrate — we carry everything your operation demands. Our prices are fixed, fair, and always posted. No surprise charges. No negotiation games.',
            "Can't find what you need? Reach out to Eddie or Josh directly and we'll make it happen.",
          ].map((text, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.12, duration: 0.45 }}
              style={{ color: 'var(--bce-text)', maxWidth: 600, lineHeight: 1.8, marginBottom: i === 0 ? '2rem' : 0, fontSize: 15 }}
            >
              {text}
            </motion.p>
          ))}
        </div>
      </motion.section>
    </div>
  );
}
